import { BillingAccountDeliveryMethod } from '../../generated/api/models.js';
import { Link, generatePath, useLocation } from 'react-router-dom';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { Table } from '../Table';
import {
  billingAccountMsg,
  eInvoiceMsg,
  emailInvoiceMsg,
  paperInvoiceMsg,
  payerMsg,
  t,
} from '../../common/i18n/index.js';
import { dsClass } from '../../common/constants/dsClasses';
import { getFullNameFromContactPerson } from '../../common/utils/contactUtils';
import { highlightKeyword } from '../../common/utils/searchFieldUtils';
import { paths } from '../../common/constants/pathVariables.js';
import type { BillingAccountSearchResponse, Contact } from '../../generated/api/models.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';

import './BillingAccountsAccordion.scss';

export interface BillingAccountsAccordionProps {
  contact: Contact;
  billingAccounts: BillingAccountSearchResponse[];
}

export const BillingAccountsAccordion = ({ contact, billingAccounts }: BillingAccountsAccordionProps) => {
  const location = useLocation();
  const contactFullName = getFullNameFromContactPerson(contact?.person);
  const deliveryMethods = {
    [BillingAccountDeliveryMethod.EMAIL]: t.NNV9(emailInvoiceMsg),
    [BillingAccountDeliveryMethod.ELECTRONIC]: t.IK1D(eInvoiceMsg),
    [BillingAccountDeliveryMethod.PAPER]: t.TER4(paperInvoiceMsg),
  };

  const cols: CLTableColumn[] = [
    { key: 'billingAccountDisplayId', label: t.IFT9(billingAccountMsg) },
    { key: 'payerName', label: t.PB6S(payerMsg) },
    { key: 'billingContactName', label: t.VYZS('Contact person') },
    { key: 'billReceiverName', label: t.KXDH('Email invoice receiver') },
  ];

  const rows = billingAccounts
    .map(searchResponse => searchResponse.result)
    .map(ba => ({
      billingAccountDisplayId: (
        <Link
          to={`${generatePath(paths.BILLING_ACCOUNT, { billingAccountId: ba.billingAccountDisplayId })}?mdmId=${ba.accountMasterId}`}
          state={{
            redirectToPath: `${location.pathname}?mdmId=${ba.accountMasterId}`,
            redirectMessage: t.YAKO('Moving back to user details'),
          }}
        >
          <div className="of-table-description">
            <div className={dsClass.TEXT_S}>{ba.billingAccountDisplayId}</div>
            <div className={dsClass.TEXT_XS}>{deliveryMethods[ba.deliveryMethod ?? '']}</div>
          </div>
        </Link>
      ),
      payerName: (
        <div className="of-table-description">
          <div className={dsClass.TEXT_S}>{ba.payerName}</div>
          <div className={dsClass.TEXT_XS}>{ba.payerNameExtension}</div>
        </div>
      ),
      billingContactName: highlightKeyword(ba.billingContactName ?? '-', contactFullName),
      billReceiverName: highlightKeyword(ba.billReceiverName ?? '-', getFullNameFromContactPerson(contact?.person)),
    }));

  return (
    <LinkableAccordion
      heading={t.ZVMK('Billing Accounts')}
      headingLevel="h3"
      badge={t.UDN6('{} pcs', billingAccounts?.length.toString() || '0')}
      className="of-billing-accounts-accordion"
      id="billing-accounts"
    >
      <p className="of-billing-accounts-accordion__description">
        {t.V62H('List shows billing accounts where the user is a contact person or email invoice receiver.')}
      </p>
      <Table columns={cols} rows={rows} noItemsText={t.VA1O('No records')} />
    </LinkableAccordion>
  );
};
